import { rollenAusFeld } from "../../data/kontaktstatusSegment";
import { collectPartnerProfile, validatePartnerProfile } from "../produktpartner/partnerProfileModel";
import { savePartnerProfile } from "../../services/produktpartnerService";
// AkteAllgemeinAnlage.tsx — Reiter „Allgemein" der Kontaktakte als Anlage-Formular
// im Bearbeiten-Modus (User 27.09.2026, docs/analyse-akte-allgemein-gleich-anlage.md).
//
// Lädt den Kontakt, baut daraus den Formular-State der Anlage
// (`contactToPrivateFormState` / `contactToCompanyFormState` + Beziehungen) und
// speichert NUR die Änderungen gegenüber dem Ladestand:
//   1. `buildAnlageUpdatePayload` → PUT contact/:id (Listen mit ids)
//   2. Firma: weitere Branchen, falls geändert
//   3. `beziehungenAbgleichen` (Sammlung, Kundenverbindung, Arbeitgeber)
//   4. neu gewählte Herkunft-Bezüge (Empfehlungsgeber/Tippgeber)
//   5. der Nachlauf des Formulars (Dateien ablegen, neuer Arbeitgeber …,
//      utils/anlageNachlauf.ts)
//
// Der Ladestand wird erst festgehalten, nachdem die Maske ihre Vorbelegungen
// gesetzt hat (Land, Geschäftsjahr …) — sonst sähe jede geöffnete Akte schon
// „geändert" aus.

import * as React from "react";
import { apiUrl } from "../../utils/api";
import type { FormState } from "../../utils/formUtils";
import { beziehungenAbgleichen, kontaktTypVon, ownershipFuerAbgleich } from "../../utils/bearbeitenBeziehungen";
import type { Ownership } from "../relationsInline/persistRelations";
import type { AnlageNachlauf } from "../../utils/anlageNachlauf";
import { HERKUNFT_ARTEN, HERKUNFT_BEZUG, bezugLesen, herkunftVerknuepfen } from "../herkunft/herkunftBezug";
import { buildAnlageUpdatePayload } from "../../utils/anlageUpdatePayload";
import { branchengruppenSpeichern } from "../../services/branchengruppeService";
import { akteAnlageLaden, felderAusKontakt } from "../../utils/akteAnlageLaden";
import {
  entscheidungenAnwenden,
  konfliktInfoAus,
  zusammenfuehren,
  type Zusammenfuehrung,
} from "../../utils/kontaktKonflikt";
import { useKontaktKonflikt } from "./KontaktKonfliktDialog";
import { useHaushaltUmzug } from "./HaushaltUmzugDialog";
import { anschriftGeaendert, haushaltsMitgliederLaden, umzugAnwenden } from "../../utils/haushaltUmzug";
import { useAkteStand } from "./useAkteStand";
import NewPrivateContactForm from "../NewPrivateContactForm";
import NewCompanyContactForm, { type LogoAkte } from "../NewCompanyContactForm";
import { KontaktSprungContext } from "../forms/KontaktSprung";
import { AkteSofortSpeichernContext, type AkteSofortSpeichern } from "../relationsInline/akteSofortSpeichern";
import { hinweisen } from "./bestaetigen";
import { useAuth } from "../../context/AuthContext";
import {
  OffeneVollanlagenContext,
  speichernWegenVollanlageBlockiert,
  useOffeneVollanlagenRegister,
} from "../forms/VollanlageWeiter";

type AnyRec = Record<string, any>;

/** Felder außerhalb des PUT-Rumpfs, die das Speichern trotzdem schreibt. */
const HERKUNFT_FELDER = new Set<string>(HERKUNFT_ARTEN.map((art) => HERKUNFT_BEZUG[art].feld));
const ZUSATZ_FELD = (k: string): boolean =>
  (k.startsWith("rel_") && k !== "rel_previousLabels" && k !== "rel_bestand" && k !== "rel_ownership") ||
  k === "vereine" ||
  k === "employerContactId" ||
  k.startsWith("pp") || HERKUNFT_FELDER.has(k);

/** JSON-Felder schlüsselsortiert vergleichen — reine Umformatierung ist keine Änderung. */
const normiert = (v: unknown): string => {
  const s = v == null ? "" : String(v);
  if (!/^[[{]/.test(s.trim())) return s.trim();
  try {
    return JSON.stringify(JSON.parse(s), (_k, x) =>
      x && typeof x === "object" && !Array.isArray(x)
        ? Object.fromEntries(Object.entries(x as Record<string, unknown>).sort(([a], [b]) => a.localeCompare(b)))
        : x,
    );
  } catch {
    return s;
  }
};

/** Was das Schloss im Aktenkopf aufruft. */
export type AkteAnlageHandle = {
  /** Gibt es ungespeicherte Änderungen? */
  geaendert: () => boolean;
  /** Speichert alle Änderungen; `false`, wenn etwas fehlt oder schiefging. */
  speichern: () => Promise<boolean>;
  /** Verwirft alle Eingaben und lädt den gespeicherten Stand neu. */
  verwerfen: () => void;
};

export const AkteAllgemeinAnlage = React.forwardRef<AkteAnlageHandle, {
  contactId: number;
  nurLesen?: boolean;
  /**
   * Schloss im Aktenkopf (User 28.09.2026): gesperrt = nur ansehen. Beim
   * Entsperren wird bearbeitet und alles zwischengespeichert, bis das Schloss
   * wieder geschlossen wird — dann speichert die Akte.
   */
  gesperrt?: boolean;
  /** Nach dem Speichern — die Akte lädt ihren Kopf neu. */
  onGespeichert?: () => void;
  /**
   * Aktuelle Verantwortlichkeit aus dem Kopf der Akte (FO/BO/persönlicher
   * Kontakt). Neu angelegte Unterkontakte bekommen sie. Ohne sie galt der
   * Stand beim Laden des Formulars — ein FO-Wechsel im Kopf kam bei neuen
   * Haushaltsmitgliedern nicht an (Befund 28.09.2026).
   */
  verantwortung?: Ownership;
  /**
   * Klick auf einen Namen in einer Beziehungsliste: zur Akte dieses Kontakts
   * (User 28.09.2026). Die Akte entscheidet, wie (Seite oder Fenster) — und
   * fragt vorher nach, wenn etwas ungespeichert ist. Ohne: kein Sprung.
   */
  onKontaktOeffnen?: (contactId: number) => void;
  onPortfolio?: () => void;
  /**
   * Der Kontakt, wie die Akte ihn schon geladen hat (`include=relations,
   * relations-deep`, Rohform der API). Zusammen mit `onNeuLaden` lädt das
   * Formular dann nicht selbst. `null` = die Akte lädt noch.
   */
  vorgeladen?: AnyRec | null;
  /** Die Akte lädt den Kontakt neu (liefert danach ein neues `vorgeladen`). */
  onNeuLaden?: () => void;
  /**
   * Logo-Vorschlag aus „Daten befüllen" (nur Firmen): ob schon ein Logo da
   * ist, und was nach dem sofortigen Speichern passiert. Siehe
   * `NewCompanyContactForm`.
   */
  logoAkte?: LogoAkte;
}>(function AkteAllgemeinAnlage(
  { contactId, nurLesen, gesperrt = false, onGespeichert, verantwortung, onKontaktOeffnen, onPortfolio, vorgeladen, onNeuLaden, logoAkte },
  ref,
) {
  const [kontakt, setKontakt] = React.useState<AnyRec | null>(null);
  const kontaktRef = React.useRef<AnyRec | null>(null);
  kontaktRef.current = kontakt;
  const vorgeladenModus = vorgeladen !== undefined && !!onNeuLaden;
  const [fehler, setFehler] = React.useState<string | null>(null);
  const [formFields, setFormFields] = React.useState<FormState>({});
  const [durchlauf, setDurchlauf] = React.useState(0);
  const geladenRef = React.useRef<FormState | null>(null);
  const ladestandOffenRef = React.useRef(false);
  const [formKey, setFormKey] = React.useState(0);
  const nachlaufRef = React.useRef<AnlageNachlauf | null>(null);
  const aktuellRef = React.useRef<FormState>({});
  aktuellRef.current = formFields;
  // Zwei Leute an derselben Akte (28.09.2026): Stand beim Laden + Rückfrage
  // bei 409 — Logik in useAkteStand / KontaktKonfliktDialog / kontaktKonflikt.ts.
  const stand = useAkteStand(contactId);
  const konflikt = useKontaktKonflikt<Zusammenfuehrung & { neu: { kontakt: AnyRec; felder: FormState } }>();
  // A4 (Plan 2026-09-30): Umzug — auch bei den Haushaltsmitgliedern ändern?
  const umzug = useHaushaltUmzug();
  const zurAkte = React.useMemo(
    () => (onKontaktOeffnen ? (id: number) => id !== contactId && onKontaktOeffnen(id) : null),
    [onKontaktOeffnen, contactId],
  );

  /** Aus dem geladenen Kontakt den Formular-State bauen und die Maske neu aufsetzen. */
  const uebernehmen = React.useCallback(
    async (c: AnyRec, abgebrochen: () => boolean) => {
      setFehler(null);
      geladenRef.current = null;
      ladestandOffenRef.current = false;
      // Dieselbe Übersetzung wie beim Nachladen im Konfliktfall (akteAnlageLaden.ts).
      const felder = await felderAusKontakt(c, contactId);
      if (abgebrochen()) return;
      stand.merken(c);
      setKontakt(c);
      setFormFields(felder);
      // Die Maske erst mit den frischen Daten neu aufbauen — sonst liefen
      // ihre Vorbelegungs-Effekte auf dem alten Stand und gingen mit dem
      // Ersetzen verloren.
      setFormKey((k) => k + 1);
      // Ladestand erst nach den Vorbelegungs-Effekten der Maske festhalten
      // (siehe Effekt unten) — es sei denn, das Entsperren war schneller.
      ladestandOffenRef.current = true;
    },
    [contactId],
  );

  // Selbst laden — nur ohne vorgeladenen Kontakt der Akte.
  React.useEffect(() => {
    if (vorgeladenModus) return;
    let abgebrochen = false;
    setFehler(null);
    geladenRef.current = null;
    ladestandOffenRef.current = false;
    (async () => {
      try {
        const res = await fetch(apiUrl(`contact/${contactId}`, { include: "relations,relations-deep" }), {
          credentials: "include",
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const c = (await res.json()) as AnyRec;
        await uebernehmen(c, () => abgebrochen);
      } catch (e) {
        if (!abgebrochen) setFehler(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => {
      abgebrochen = true;
    };
  }, [contactId, durchlauf, vorgeladenModus, uebernehmen]);

  // Vorgeladen (Befund 28.09.2026, Ladezeit): Die Akte hat denselben Kontakt
  // mit denselben `include`s schon geholt. Bis dahin lud das Formular ihn
  // danach ein zweites Mal — hintereinander, die teuerste Abfrage der Seite
  // doppelt. Ein neuer Stand der Akte (nach dem Speichern, Verwerfen oder
  // einer Änderung im Kopf) wird übernommen — aber nie über ungespeicherte
  // Eingaben hinweg, es sei denn, das Formular hat ihn selbst angefordert.
  const nachladenAngefordertRef = React.useRef(false);
  const hatAenderungenRef = React.useRef<() => boolean>(() => false);
  React.useEffect(() => {
    if (!vorgeladenModus || !vorgeladen || Number(vorgeladen.id) !== contactId) return;
    if (kontaktRef.current && !nachladenAngefordertRef.current && hatAenderungenRef.current()) return;
    nachladenAngefordertRef.current = false;
    let abgebrochen = false;
    void uebernehmen(vorgeladen, () => abgebrochen);
    return () => {
      abgebrochen = true;
    };
  }, [vorgeladenModus, vorgeladen, contactId, uebernehmen]);

  // Ladestand festhalten, sobald die Maske nach dem Laden zur Ruhe gekommen
  // ist: Jede Vorbelegung (Land, Geschäftsjahr …) ändert `formFields` und
  // startet die Wartezeit neu; erst 250 ms ohne Änderung gelten als Bestand.
  // Bis 28.09.2026 lief stattdessen ein fester 400-ms-Timer ab dem Laden —
  // dauerte das erste Rendern der großen Maske länger (langsamer Rechner),
  // hielt er einen LEEREN Stand fest, und jede Akte sah „geändert" aus.
  React.useEffect(() => {
    if (!ladestandOffenRef.current || !kontakt) return;
    const t = window.setTimeout(() => {
      if (!ladestandOffenRef.current) return;
      ladestandOffenRef.current = false;
      if (!geladenRef.current) geladenRef.current = { ...aktuellRef.current };
    }, 250);
    return () => window.clearTimeout(t);
  }, [formFields, kontakt]);

  // Spätestens beim Entsperren gilt der Ladestand als festgehalten: Solange
  // das Schloss zu ist, kann niemand etwas eingeben — was jetzt in der Maske
  // steht, ist der Bestand (samt Vorbelegungen). Ohne das nahm der Ladestand-Timer
  // eine schnelle erste Eingabe mit in den Ladestand, und das Sperren fand
  // keinen Unterschied — die Änderung ging still verloren (E2E 28.09.2026).
  // Nur der Wechsel zu → auf zählt; wird bei offenem Schloss geladen, bleibt
  // es beim Timer (die Vorbelegungs-Effekte brauchen einen Moment).
  const warGesperrtRef = React.useRef(gesperrt);
  React.useEffect(() => {
    const entsperrt = warGesperrtRef.current && !gesperrt;
    warGesperrtRef.current = gesperrt;
    if (entsperrt && kontakt && !geladenRef.current) geladenRef.current = { ...aktuellRef.current };
  }, [gesperrt, kontakt]);

  const updateField = React.useCallback((name: string, value: string) => {
    setFormFields((alt) => ({ ...alt, [name]: value }));
  }, []);

  const typ = kontakt ? kontaktTypVon(kontakt) : null;

  const onSave = (): Promise<number | false> => speichernMit(geladenRef.current ?? formFields, formFields);

  /**
   * Speichert den Unterschied `geladen` → `formFields`. Hat inzwischen jemand
   * anderes gespeichert (409), fragt der Konflikt-Dialog und es geht — nach
   * „anwenden" — mit dem neuen Stand als Ladestand erneut hier durch.
   */
  const speichernMit = async (geladen: FormState, formFields: FormState): Promise<number | false> => {
    if (!typ || nurLesen) return false;
    const { body, branchengruppen, geaendert } = buildAnlageUpdatePayload({ typ, geladen, aktuell: formFields });
    try {
      const fProfil = (key: string) => String(formFields[key] ?? "");
      const primaerAp = !fProfil("kontaktStatusAuch") && ["Ansprechpartner Produktpartner", "Ansprechpartner Kooperationspartner"].includes(rollenAusFeld(fProfil("kontaktStatusRollen"))[0]);
      const profilFehler = primaerAp ? validatePartnerProfile(fProfil) : [];
      if (profilFehler.length) throw new Error(profilFehler.join(" "));
      if (geaendert && Object.keys(body).length > 0) {
        const res = await fetch(apiUrl(`contact/${contactId}`), {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify(stand.rumpf(body)),
        });
        if (res.status === 409) {
          const info = konfliktInfoAus(await res.json().catch(() => null));
          if (info) return await konfliktKlaeren(info, geladen, formFields);
        }
        if (!res.ok) throw new Error(`Speichern fehlgeschlagen (HTTP ${res.status})`);
        stand.gespeichert(await res.json().catch(() => null));
      }
      if (branchengruppen) await branchengruppenSpeichern(contactId, branchengruppen as any);
      const f = (k: string) => String(formFields[k] ?? "");
      const ownership = ownershipFuerAbgleich(formFields, verantwortung ?? {});
      if (Object.keys(formFields).some(k => k.startsWith("pp") && normiert(formFields[k]) !== normiert(geladen[k]))) {
        await savePartnerProfile(contactId, collectPartnerProfile(k => String(formFields[k] ?? "")));
      }
      const nachlauf = nachlaufRef.current;
      const report = await beziehungenAbgleichen({
        contactId,
        typ,
        geladen,
        aktuell: formFields,
        ownership,
        arbeitgeberUeberspringen: !!nachlauf?.arbeitgeberNeu,
        householdAddress: {
          street: f("street") || undefined,
          postalCode: f("postalcode") || f("postalCode") || undefined,
          city: f("city") || undefined,
          ortsteil: f("cityArea") || undefined,
          country: f("country") || undefined,
        },
        householdGroupName: `Haushalt${f("lastname").trim() ? " " + f("lastname").trim() : ""}`,
      });
      // Herkunft-Bezüge: nur, was in der Akte neu gewählt wurde. Der Bestand
      // wird nicht vorbelegt — ein unverändertes Feld ist also leer, und ein
      // bereits verknüpfter Empfehlungsgeber wird nicht ein zweites Mal verknüpft.
      const neueHerkunft = HERKUNFT_ARTEN.filter((art) => {
        const feld = HERKUNFT_BEZUG[art].feld;
        return String(formFields[feld] ?? "") !== String(geladen[feld] ?? "") && bezugLesen(formFields[feld]) != null;
      });
      if (neueHerkunft.length) {
        report.failures.push(...(await herkunftVerknuepfen(contactId, formFields, neueHerkunft)));
      }
      if (nachlauf) report.failures.push(...(await nachlauf.ausfuehren(contactId, ownership)));
      // A4: Die Standardanschrift hat sich geändert → fragen, bei welchen
      // Haushaltsmitgliedern sie mitziehen soll. Erst nach dem Beziehungs-
      // Abgleich, damit eben hinzugefügte Mitglieder mitgefragt werden.
      if (typ !== "Firma" && anschriftGeaendert(geladen, formFields)) {
        const mitglieder = await haushaltsMitgliederLaden(contactId);
        if (mitglieder.length) {
          const gewaehlt = await umzug.fragen(mitglieder);
          if (gewaehlt.length) report.failures.push(...(await umzugAnwenden(contactId, gewaehlt)));
        }
      }
      if (report.failures.length) {
        await hinweisen({
          titel: "Gespeichert – mit Lücken",
          text: "Die Änderungen sind gespeichert, aber:\n\n" + report.failures.join("\n"),
          ton: "danger",
        });
      }
      return contactId;
    } catch (e) {
      await hinweisen({
        titel: "Speichern fehlgeschlagen",
        text: e instanceof Error ? e.message : "Die Änderungen konnten nicht gespeichert werden.",
        ton: "danger",
      });
      return false;
    }
  };

  const neuLaden = () => {
    if (vorgeladenModus) {
      nachladenAngefordertRef.current = true;
      onNeuLaden!();
    } else {
      setDurchlauf((d) => d + 1);
    }
  };

  /** 409: fragen — anwenden (neu speichern), verwerfen (neu laden) oder abbrechen. */
  const konfliktKlaeren = async (
    info: NonNullable<ReturnType<typeof konfliktInfoAus>>,
    geladen: FormState,
    eigene: FormState,
  ): Promise<number | false> => {
    const e = await konflikt.klaeren(info, async () => {
      const neu = await akteAnlageLaden(contactId);
      return { neu, ...zusammenfuehren({ geladen, aktuell: eigene, neu: neu.felder }) };
    });
    if (e.wahl === "abbrechen") return false;
    if (e.wahl === "verwerfen") {
      // Der neue Stand gilt; nichts Eigenes mehr offen → wie gespeichert.
      neuLaden();
      return contactId;
    }
    const { neu } = e.vorbereitet;
    const zusammen = entscheidungenAnwenden(e.vorbereitet, neu.felder, e.derenBehalten);
    stand.merken(neu.kontakt);
    geladenRef.current = neu.felder;
    setKontakt(neu.kontakt);
    setFormFields(zusammen);
    return speichernMit(neu.felder, zusammen);
  };

  const hatAenderungen = (): boolean => {
    if (!typ || !geladenRef.current) return false;
    const geladen = geladenRef.current;
    const aktuell = aktuellRef.current;
    const { geaendert, body } = buildAnlageUpdatePayload({ typ, geladen, aktuell });
    if (geaendert || Object.keys(body).length > 0 || nachlaufRef.current?.geaendert?.()) return true;
    // Was nicht im PUT steht, aber beim Speichern geschrieben wird: Beziehungen,
    // Vereine, Arbeitgeber, Kundenverbindung, neu gewählte Herkunft-Bezüge
    // (Befund 28.09.2026 — ein neues Haushaltsmitglied allein zählte sonst
    // nicht als Änderung, und die Akte ließ sich ohne Rückfrage verlassen).
    return Object.keys({ ...geladen, ...aktuell }).some(
      (k) => ZUSATZ_FELD(k) && normiert(geladen[k]) !== normiert(aktuell[k]),
    );
  };

  hatAenderungenRef.current = hatAenderungen;

  // Sofort-Speichern der Karte „Verantwortung fürs Unternehmen" (User
  // 30.09.2026, relationsInline/akteSofortSpeichern.ts): nur mit Schreibrecht,
  // nie über ungespeicherte Eingaben hinweg, danach neu laden.
  const neuLadenRef = React.useRef(neuLaden);
  neuLadenRef.current = neuLaden;
  const ansichtRef = React.useRef<Record<string, unknown>>({});
  // Kontakt löschen aus den Beziehungslisten (Paket A2): dieselben Rechte wie
  // die Kontaktliste (ContactTableRedesign `canDelete`).
  const auth = useAuth();
  const darfKontakteLoeschen = !!(auth.isAdmin || auth.isGfgl || auth.canTempDeleteContacts);
  const sofort = React.useMemo<AkteSofortSpeichern>(
    () => ({
      darfSchreiben: !nurLesen,
      darfKontakteLoeschen,
      hatUngespeicherte: () => hatAenderungenRef.current(),
      neuLaden: () => neuLadenRef.current(),
      ansicht: ansichtRef,
    }),
    [nurLesen, darfKontakteLoeschen],
  );
  // Schloss zu: Eine inline bearbeitete Zeile hätte dann nur noch gesperrte Felder.
  React.useEffect(() => {
    if (!gesperrt) return;
    for (const k of Object.keys(ansichtRef.current)) if (k.startsWith("inline-")) delete ansichtRef.current[k];
  }, [gesperrt]);

  // Vollanlagen, die aus den Schnellanlagen dieser Akte offen sind — das
  // Schloss speichert erst, wenn sie übernommen oder abgebrochen sind.
  const offeneVollanlagen = useOffeneVollanlagenRegister();

  React.useImperativeHandle(ref, () => ({
    geaendert: hatAenderungen,
    verwerfen: neuLaden,
    speichern: async () => {
      if (!typ || nurLesen) return true;
      if (await speichernWegenVollanlageBlockiert(offeneVollanlagen)) return false;
      const f = (k: string) => String(aktuellRef.current[k] ?? "").trim();
      const fehlt =
        typ === "Firma"
          ? [!f("name") && "Firmenname"].filter(Boolean)
          : [!f("lastname") && "Nachname", !f("firstname") && "Vorname"].filter(Boolean);
      if (fehlt.length) {
        await hinweisen({ titel: "Angaben fehlen", text: "Bitte zuerst ausfüllen: " + fehlt.join(", ") });
        return false;
      }
      const id = await onSave();
      if (id === false) return false;
      neuLaden();
      onGespeichert?.();
      return true;
    },
  }));

  if (fehler) return <div className="empty">Kontakt konnte nicht geladen werden ({fehler}).</div>;
  if (!kontakt || !typ) return <div className="empty">Kontaktdaten werden geladen …</div>;

  const gemeinsam = {
    formFields,
    updateField,
    onSave,
    onSaveSuccess: () => {
      neuLaden();
      onGespeichert?.();
    },
    onClose: neuLaden,
    bearbeiten: { contactId, nurLesen, gesperrt, onPortfolio, nachlauf: nachlaufRef },
  };
  // `fieldset disabled` sperrt jedes Eingabefeld und jeden Knopf der Maske auf
  // einmal — ohne dass jede Karte einen Nur-Lesen-Modus kennen muss. Einen
  // Hinweis „Gesperrt …" gibt es nicht mehr (User 28.09.2026): das Schloss
  // oben rechts sagt es selbst.
  return (
    <KontaktSprungContext.Provider value={zurAkte}>
    <AkteSofortSpeichernContext.Provider value={sofort}>
    <OffeneVollanlagenContext.Provider value={offeneVollanlagen}>
    <fieldset
      disabled={gesperrt || nurLesen}
      style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
      aria-label={gesperrt ? "Gesperrt — zum Bearbeiten das Schloss öffnen" : undefined}
    >
      {typ === "Firma" ? (
        <NewCompanyContactForm key={`f-${formKey}`} {...gemeinsam} logoAkte={logoAkte} />
      ) : (
        <NewPrivateContactForm key={`p-${formKey}`} {...gemeinsam} />
      )}
    </fieldset>
    </OffeneVollanlagenContext.Provider>
    </AkteSofortSpeichernContext.Provider>
    {konflikt.dialog}
    {umzug.dialog}
    </KontaktSprungContext.Provider>
  );
});
